import React, { useState, useEffect } from 'react';
import { X, Car, Plane, Compass, Users, Shield, ChevronRight, ChevronLeft } from 'lucide-react';

const SERVICES = [
  { id: 'chauffeur', icon: Car, name: 'Private Chauffeur', desc: 'Hourly, daily or multi-day service' },
  { id: 'airport', icon: Plane, name: 'Airport Transfers', desc: 'Meet & greet, private pickup' },
  { id: 'concierge', icon: Compass, name: 'Concierge & Experiences', desc: 'Restaurants, shopping, yacht, bespoke' },
  { id: 'groups', icon: Users, name: 'Groups & Delegations', desc: 'Events, teams, coordinated transport' },
  { id: 'protection', icon: Shield, name: 'Close Protection', desc: 'Personal security on request' },
];

const BookingModal = ({ isOpen, onClose, preselectedService, preselectedVehicle, onTrackQuote }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service: preselectedService || '',
    vehicle: preselectedVehicle || '',
    date: '',
    duration: '',
    passengers: '',
    luggage: '',
    pickup: '',
    dropoff: '',
    notes: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setForm({
      service: preselectedService || '', vehicle: preselectedVehicle || '', date: '', duration: '',
      passengers: '', luggage: '', pickup: '', dropoff: '', notes: '',
    });
  }, [isOpen, preselectedService, preselectedVehicle]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep(preselectedService ? 2 : 1);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, preselectedService]);

  if (!isOpen) return null;

  const totalSteps = 4;
  const selectedService = SERVICES.find(s => s.id === form.service);

  const update = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const buildWhatsAppMessage = () => {
    let msg = `Hello, I'd like to request a service from ASSIST VIP.\n\n`;
    if (selectedService) msg += `Service: ${selectedService.name}\n`;
    if (form.vehicle) msg += `Vehicle: ${form.vehicle}\n`;
    if (form.date) msg += `Date: ${form.date}\n`;
    if (form.duration) msg += `Duration: ${form.duration}\n`;
    if (form.passengers) msg += `Passengers: ${form.passengers}\n`;
    if (form.luggage) msg += `Luggage: ${form.luggage}\n`;
    if (form.pickup) msg += `Pickup: ${form.pickup}\n`;
    if (form.dropoff) msg += `Dropoff: ${form.dropoff}\n`;
    if (form.notes) msg += `Notes: ${form.notes}\n`;
    return encodeURIComponent(msg);
  };

  const sendToWhatsApp = () => {
    if (onTrackQuote) onTrackQuote(form.service);
    window.open(`https://wa.me/905447153414?text=${buildWhatsAppMessage()}`, '_blank');
    onClose();
  };

  return (
    <div className="booking-overlay" data-testid="booking-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="booking-modal" data-testid="booking-modal">
        <button className="booking-close" onClick={onClose} data-testid="booking-close"><X size={20} /></button>

        <div className="booking-header">
          <div className="booking-step-label">Step {step} of {totalSteps}</div>
          <h2 className="booking-title">
            {step === 1 && 'Select Your Service'}
            {step === 2 && 'Date & Details'}
            {step === 3 && 'Pickup & Route'}
            {step === 4 && 'Review & Send'}
          </h2>
          <p className="booking-subtitle">
            {step === 1 && 'Choose the service that fits your needs'}
            {step === 2 && 'Tell us when and how many'}
            {step === 3 && 'Where should we meet you?'}
            {step === 4 && 'Confirm your request via WhatsApp'}
          </p>
        </div>

        <div className="booking-progress">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={`booking-progress-dot ${i <= step ? 'active' : ''}`} />
          ))}
        </div>

        <div className="booking-body">
          {step === 1 && (
            <div className="booking-options" data-testid="booking-step-1">
              {SERVICES.map(s => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.id}
                    className={`booking-option ${form.service === s.id ? 'selected' : ''}`}
                    onClick={() => update('service', s.id)}
                    data-testid={`booking-option-${s.id}`}
                  >
                    <div className="booking-option-icon"><Icon size={22} /></div>
                    <div>
                      <h4>{s.name}</h4>
                      <p>{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div data-testid="booking-step-2">
              <div className="booking-row">
                <div className="booking-field">
                  <label>Date</label>
                  <input type="date" value={form.date} onChange={e => update('date', e.target.value)} />
                </div>
                <div className="booking-field">
                  <label>Duration</label>
                  <select value={form.duration} onChange={e => update('duration', e.target.value)}>
                    <option value="">Select</option>
                    <option value="One-way transfer">One-way transfer</option>
                    <option value="Half day (4h)">Half day (4h)</option>
                    <option value="Full day (8h)">Full day (8h)</option>
                    <option value="Multi-day">Multi-day</option>
                  </select>
                </div>
              </div>
              <div className="booking-row">
                <div className="booking-field">
                  <label>Passengers</label>
                  <select value={form.passengers} onChange={e => update('passengers', e.target.value)}>
                    <option value="">Select</option>
                    {[1,2,3,4,5,6,7,8,'9+'].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div className="booking-field">
                  <label>Luggage</label>
                  <select value={form.luggage} onChange={e => update('luggage', e.target.value)}>
                    <option value="">Select</option>
                    <option value="None">None</option>
                    <option value="Light (1-2 bags)">Light (1-2 bags)</option>
                    <option value="Medium (3-5 bags)">Medium (3-5 bags)</option>
                    <option value="Heavy (6+ bags)">Heavy (6+ bags)</option>
                  </select>
                </div>
              </div>
              {form.vehicle && (
                <div className="booking-field">
                  <label>Preferred Vehicle</label>
                  <input type="text" value={form.vehicle} readOnly style={{ opacity: 0.7 }} />
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="booking-step-3">
              <div className="booking-field">
                <label>Pickup Location</label>
                <input
                  type="text"
                  placeholder={form.service === 'airport' ? 'e.g. Istanbul Airport (IST)' : 'Hotel name, address or landmark'}
                  value={form.pickup}
                  onChange={e => update('pickup', e.target.value)}
                />
              </div>
              <div className="booking-field">
                <label>Destination</label>
                <input
                  type="text"
                  placeholder="Where are you heading?"
                  value={form.dropoff}
                  onChange={e => update('dropoff', e.target.value)}
                />
              </div>
              <div className="booking-field">
                <label>Special Requests</label>
                <textarea
                  placeholder="Child seat, specific route, privacy needs..."
                  value={form.notes}
                  onChange={e => update('notes', e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="booking-step-4">
              <div className="booking-summary">
                {selectedService && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Service</span>
                    <span className="booking-summary-value">{selectedService.name}</span>
                  </div>
                )}
                {form.vehicle && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Vehicle</span>
                    <span className="booking-summary-value">{form.vehicle}</span>
                  </div>
                )}
                {form.date && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Date</span>
                    <span className="booking-summary-value">{form.date}</span>
                  </div>
                )}
                {form.duration && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Duration</span>
                    <span className="booking-summary-value">{form.duration}</span>
                  </div>
                )}
                {form.passengers && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Passengers</span>
                    <span className="booking-summary-value">{form.passengers}</span>
                  </div>
                )}
                {form.luggage && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Luggage</span>
                    <span className="booking-summary-value">{form.luggage}</span>
                  </div>
                )}
                {form.pickup && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Pickup</span>
                    <span className="booking-summary-value">{form.pickup}</span>
                  </div>
                )}
                {form.dropoff && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Destination</span>
                    <span className="booking-summary-value">{form.dropoff}</span>
                  </div>
                )}
                {form.notes && (
                  <div className="booking-summary-row">
                    <span className="booking-summary-label">Notes</span>
                    <span className="booking-summary-value">{form.notes}</span>
                  </div>
                )}
              </div>

              <button className="booking-whatsapp-btn" onClick={sendToWhatsApp} data-testid="booking-whatsapp-submit">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.34 0-4.512-.728-6.305-1.97l-.44-.31-2.642.886.886-2.642-.31-.44A9.935 9.935 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                </svg>
                Send via WhatsApp
              </button>

              <p className="booking-note">
                Your request will be sent to our concierge team. We will confirm availability and provide a quote within minutes.
              </p>
            </div>
          )}

          {step < 4 && (
            <div className="booking-actions">
              {step > 1 && (
                <button className="btn-outline" onClick={() => setStep(step - 1)} data-testid="booking-back">
                  <ChevronLeft size={16} style={{ marginRight: 4 }} /> Back
                </button>
              )}
              <button
                className="btn-champagne"
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !form.service}
                style={step === 1 && !form.service ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                data-testid={`booking-step-${step}-next`}
              >
                Continue <ChevronRight size={16} style={{ marginLeft: 4 }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
